// Grids & Gantts — sorting a TREE by one of its columns.
//
// Standard 1.8: A SORT REORDERS SIBLINGS. IT NEVER FLATTENS.
//
// Clicking a column heading on an ordinary table sorts the rows. On a
// structure it cannot: the rows are a tree, and putting them in value order
// would throw away the parents, the folders and the groups that are the whole
// point of the page. What a reader means by "sort by due date" here is
// "inside each folder, and under each parent, put them in due-date order".
//
// So the flattened row list is re-ordered in place, one sibling group at a
// time, with every child following the row it belongs to. A row's parent is
// the nearest row ABOVE it at a shallower `level` — the order and the depth
// the engine printed. NOT its `path` less the last segment: a group value
// may itself hold a "/" ("Mining/Industrial"), and a folder's rule lines are
// `rule/<folderId>/<i>`, so splitting the path put rows under parents they
// do not have (or at the very top). A row with no level (a hand-built list)
// is as deep as its path has slashes.
//
// FOLDERS DO NOT MOVE. A structure's folders are in the order the page was
// built in, on purpose; a sort orders what is IN a folder, the way the
// structure this page replaces does — never the folders themselves.
//
// Pure, and tested under node: the part that can be wrong is "did a child end
// up under a different parent", and that is not something you can see by
// looking at a screen.

// What one row is worth in one column. The grid already knows — `cellOf` is
// the single place that answers what a cell CONTAINS — so the comparable
// value comes from there rather than from a second, drifting copy.
export function sortKeyOf(cell) {
  if (!cell) return { n: null, s: '' };
  // A number sorts as a number; everything else sorts as text, case-folded.
  // `sortNum` is a cell's number FOR SORTING ONLY, when what it prints is
  // words ("Overdue", "2w 3d") and a `num` would be read as its value.
  if (cell.sortNum != null && Number.isFinite(cell.sortNum)) return { n: cell.sortNum, s: '' };
  if (cell.num != null && Number.isFinite(cell.num)) return { n: cell.num, s: '' };
  const raw = cell.raw != null ? cell.raw : cell.text;
  const s = String(raw == null ? '' : raw);
  return { n: null, s: s.toLowerCase() };
}

// EMPTY SORTS LAST, ascending or descending. A blank is not "before
// everything" — it is "not answered", and a reader sorting by due date wants
// the dated work, not forty blanks at the top.
export function compareKeys(a, b, dir = 'asc') {
  const aEmpty = a.n == null && !a.s;
  const bEmpty = b.n == null && !b.s;
  if (aEmpty || bEmpty) {
    if (aEmpty && bEmpty) return 0;
    return aEmpty ? 1 : -1;
  }
  let d = 0;
  if (a.n != null && b.n != null) d = a.n - b.n;
  // Text the way a person reads it: WCM-9 before WCM-10 before WCM-100.
  else d = TEXT.compare(a.s, b.s);
  return dir === 'desc' ? -d : d;
}

const TEXT = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

const depthOf = (r) => (Number.isFinite(r && r.level) ? r.level : (String((r && r.path) || '').match(/\//g) || []).length);
const FIXED = (r) => r.kind === 'rule' || r.kind === 'folder';

// The flattened rows, re-ordered. `keyFor(row)` gives the value to compare;
// rows it cannot answer for (a generator line, a group header) keep their
// place relative to their own kind.
export function sortRows(rows, keyFor, dir = 'asc') {
  const list = rows || [];
  if (!list.length || !dir) return list;

  // Each row's children, in the order they arrive: a row's descendants are
  // the block that follows it until a row at its own depth or shallower.
  const childrenOf = new Map();
  const roots = [];
  const stack = [];
  list.forEach((r) => {
    const d = depthOf(r);
    while (stack.length && stack[stack.length - 1].d >= d) stack.pop();
    const parent = stack.length ? stack[stack.length - 1].r : null;
    if (parent) {
      if (!childrenOf.has(parent)) childrenOf.set(parent, []);
      childrenOf.get(parent).push(r);
    } else {
      roots.push(r);
    }
    stack.push({ r, d });
  });

  const sortGroup = (group) => {
    // A RULE LINE AND A FOLDER ARE NOT ROWS TO SORT. Generators are printed
    // above what they generate, and folders sit in the order the page was
    // built in; both keep their places and the rest are sorted around them.
    const keyed = group.filter((r) => !FIXED(r)).map((r, i) => ({ r, i, k: keyFor(r) }));
    keyed.sort((x, y) => compareKeys(x.k, y.k, dir) || (x.i - y.i));
    let next = 0;
    return group.map((r) => (FIXED(r) ? r : keyed[next++].r));
  };

  const out = [];
  const walk = (group) => {
    sortGroup(group).forEach((r) => {
      out.push(r);
      const kids = childrenOf.get(r);
      if (kids && kids.length) walk(kids);
    });
  };
  walk(roots);
  // A row whose parent was not in the list at all would otherwise vanish;
  // the count must come out the same or the sort has eaten something.
  return out.length === list.length ? out : list;
}
